import { Component, OnDestroy, OnInit } from '@angular/core';
import { ApronService } from '../apron.service';
import { switchMap, takeWhile } from 'rxjs/operators';
import { ActivatedRoute, Router } from "@angular/router";
import { ApronDto, ApproveOperationDto, MarkPendingDto } from "@up/data";
import { Subscription, timer } from "rxjs";

interface ShiftData {
  transactionId: string;
  employeeName: string;
  floatAmount: number;
  cashFromSales: number;
  timestamp: Date;
  avatarUrl?: string;
}

type AppState = 'PIN' | 'FLOAT_DETAILS' | 'QR_CODE' | 'HANDOVER_COMPLETE';

@Component({
  selector: 'up-start-shift',
  templateUrl: './start-shift.component.html',
  styleUrls: ['./start-shift.component.scss']
})
export class StartShiftComponent implements OnInit, OnDestroy {

  // --- STATE ---
  currentState: AppState = 'PIN'; // Start at PIN

  // Notification Logic
  showNotification = false;
  notificationTitle = '';
  notificationMessage = '';
  notificationType: 'error' | 'warning' | 'info' = 'error';

  // Data Logic
  data: ShiftData | null = null;
  isLoading = true;
  showModal = false;
  apron: ApronDto;
  apronId: string;
  costCentreId: number;
  managerEmployeeId: number;
  subscription: Subscription;

  constructor(private apronService: ApronService,
              private route: ActivatedRoute,
              private router: Router) {
    this.apronId = this.route.snapshot.paramMap.get('apronId');
  }

  ngOnInit() {
    this.fetchData();
    this.checkAuth();
  }

  // --- 1. PIN & AUTHENTICATION ---
  checkAuth() {
    const storedAuth = localStorage.getItem('pin_auth');
    const storedManagerId = localStorage.getItem('manager_employee_id');
    if (storedAuth === 'true' && storedManagerId) {
      this.managerEmployeeId = Number(storedManagerId);
      this.currentState = 'FLOAT_DETAILS';
      this.markAsPending();
    } else {
      this.currentState = 'PIN';
    }
  }

  onPinValidated(managerId: number) {
    this.managerEmployeeId = managerId;
    this.currentState = 'FLOAT_DETAILS';
    this.markAsPending();
  }

  private markAsPending() {
    const markPendingDto: MarkPendingDto = {
      managerEmployeeId: this.managerEmployeeId
    };

    this.apronService.markStartPending(Number(this.apronId), markPendingDto).subscribe(
      () => {
        this.currentState = 'FLOAT_DETAILS';
      },
      (error) => {
        console.log(error);
        const status = error?.statusCode;

        if (status === 409) {
          // Conflict - Status already approved or not in the expected state
          this.showNotificationMessage(
            'Shift Start Already Started',
            'This shift start process has already started and may have been completed already.',
            'warning'
          );
        } else {
          // Other errors
          // Keep UI quiet; failure is handled by state
        }
      }
    );
  }

  // --- 2. DATA & MODAL ---
  get totalAmount(): number {
    return this.data ? this.data.floatAmount + this.data.cashFromSales : 0;
  }

  fetchData() {
    this.isLoading = true;
    this.apronService.getById(this.apronId).subscribe(
      async (apron) => {
        if (apron.status === 'start-shift approved') {
          this.currentState = 'QR_CODE';
          this.checkIfAcknowledged();
        } else if (apron.status.includes('acknowledged') || apron.status.includes('approved')) {
          this.currentState = 'HANDOVER_COMPLETE';
        }
        this.apron = apron;
        this.data = {
          transactionId: apron.id.toString(),
          employeeName: apron.operator?.firstName + ' ' + apron.operator?.lastName,
          floatAmount: apron.expectedAmount,
          avatarUrl: apron.operator.imageUrl,
          cashFromSales: 0.00,
          timestamp: apron.createdDate
        }
        this.costCentreId = apron.merchant?.costCentreId;
        this.isLoading = false;
      },
      (error) => {
        this.isLoading = false;
        console.log(error);
        this.router.navigate(['/apron-error']);
        return;
      }
    );
  }

  openModal() {
    this.showModal = true;
  }

  closeModal() {
    this.showModal = false;
  }

  showNotificationMessage(title: string, message: string, type: 'error' | 'warning' | 'info' = 'error') {
    this.notificationTitle = title;
    this.notificationMessage = message;
    this.notificationType = type;
    this.showNotification = true;

    // Auto-hide after 10 seconds
    setTimeout(() => {
      this.hideNotification();
    }, 10000);
  }

  hideNotification() {
    this.showNotification = false;
    this.notificationTitle = '';
    this.notificationMessage = '';
  }

  checkIfAcknowledged() {
    this.subscription = timer(0, 5000).pipe(
      switchMap(() => this.apronService.getById(this.apronId)),
      takeWhile(apron => !apron.status.includes('acknowledged'), true)
    ).subscribe((apron) => {
      if (apron.status.includes('acknowledged')) {
        this.currentState = 'HANDOVER_COMPLETE';
      }
    });
  }

  confirmHandover() {
    const approveDto: ApproveOperationDto = {
      managerEmployeeId: this.managerEmployeeId,
      amount: this.totalAmount,
      note: 'Ok on start'
    };

    this.apronService.approveStart(Number(this.apronId), approveDto).subscribe(
      () => {
        this.closeModal();
        this.currentState = 'QR_CODE';
        this.checkIfAcknowledged();
      },
      (error) => {
        console.log(error);
        this.showNotificationMessage(
          'Failed to approve start shift.',
          'Please try again.',
          'error'
        );
        this.closeModal();

        const status = error?.statusCode;

        if (status === 500 || status === 409) {
          // Internal Server Error - Status issue
          this.showNotificationMessage(
            'Unable to Process Request',
            'The shift status cannot be changed at this time. The shift may have already started for this employee. Please contact support if this issue persists.',
            'error'
          );
        }
      }
    );
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
